import React from 'react';
import { ArrowLeft, Users, Target, Heart, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import TaveelLogo from '../components/TaveelLogo';

const WhyTaveel = () => {
  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a1a]/95 backdrop-blur-sm border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => window.location.href = '/'}>
            <TaveelLogo className="w-8 h-8" />
            <div className="text-2xl font-bold text-[#10B981]">TAVEEL</div>
          </div>
          <Button 
            onClick={() => window.location.href = '/'}
            className="bg-transparent hover:bg-[#10B981]/10 text-[#10B981] border border-[#10B981]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            WHY <span className="text-[#10B981]">TAVEEL</span>?
          </h1>
          <p className="text-xl text-gray-400 mb-8">
            Two law students who saw the gap between good ads and great ones—and decided to bridge it.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-12 px-6 bg-[#2d2d2d]">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-[#1a1a1a] border-gray-700">
            <CardContent className="p-12">
              <h2 className="text-3xl font-bold mb-6 text-[#10B981]">The Beginning</h2>
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                <p>
                  It started in high school—two friends, <strong className="text-white">Mohanned Fadeel</strong> and <strong className="text-white">Omid Tavakoli</strong>, 
                  bonding over late-night conversations about business, marketing, and the future. While their classmates 
                  were worrying about weekend plans, Mohanned and Omid were dissecting Super Bowl commercials and analyzing 
                  why some brands dominated while others faded into obscurity.
                </p>
                
                <p>
                  Fast forward to today: Mohanned is studying law at the <strong className="text-white">University of Toronto</strong>, 
                  while Omid pursues his legal studies at <strong className="text-white">Western University</strong>. Different schools, 
                  same vision. Both witnessed firsthand how local businesses—talented, hardworking entrepreneurs—were being 
                  held back not by their products or services, but by their marketing.
                </p>

                <p>
                  <strong className="text-white">The ads were terrible.</strong> Generic stock photos. Boring copy. 
                  Zero personality. Meanwhile, massive brands were crushing it with creative, data-driven campaigns that 
                  spoke directly to their audiences.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* The Turning Point */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-[#2d2d2d] border-gray-700">
            <CardContent className="p-12">
              <h2 className="text-3xl font-bold mb-6 text-[#10B981]">The Turning Point</h2>
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                <p>
                  Between law school lectures and late-night study sessions, Mohanned and Omid started freelancing—helping 
                  local businesses with their marketing. <strong className="text-white">What began as a side project 
                  became an obsession.</strong>
                </p>
                
                <p>
                  They spent years in the trenches—running Facebook ads at 2 AM, analyzing Google Analytics over coffee, 
                  split-testing landing pages during exam breaks. Sales. Marketing. Strategy. They learned what actually 
                  worked, not what marketing gurus <em>said</em> worked.
                </p>

                <p>
                  <strong className="text-white">They spotted a pattern:</strong> successful ads weren't magic. They followed 
                  formulas. They leveraged psychology. They told stories. Most importantly, they were <em>authentic</em>. 
                  Not stock photos. Not corporate jargon. Real businesses showing real value.
                </p>

                <p>
                  So they decided to build something bigger. <strong className="text-white">TAVEEL</strong> was born—named 
                  from Omid's last name, representing the partnership that started in a high school hallway and evolved 
                  into a mission.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* The Mission */}
      <section className="py-12 px-6 bg-[#2d2d2d]">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-[#1a1a1a] border-gray-700">
            <CardContent className="p-12">
              <h2 className="text-3xl font-bold mb-6 text-[#10B981]">The Mission</h2>
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                <p>
                  <strong className="text-white">We're not your typical agency.</strong> We're not wearing suits in a 
                  corporate office, throwing buzzwords at you. We're two law students who are <em>obsessed</em> with 
                  helping businesses grow through marketing that actually converts.
                </p>
                
                <p>
                  Here's the truth: most businesses don't fail because of their product. They fail because nobody knows 
                  about them. They're drowned out by competitors with bigger budgets and better marketing. <strong className="text-white">
                  That's not fair. And we're here to change it.</strong>
                </p>

                <p>
                  TAVEEL exists to level the playing field. We bring enterprise-level marketing—AI-powered trend analysis, 
                  professional photography and videography, data-driven strategies—to businesses that couldn't otherwise 
                  afford it. We've scaled over <strong className="text-white">100+ businesses</strong> across <strong className="text-white">
                  16+ industries</strong>, and we're just getting started.
                </p>

                <p>
                  The name <strong className="text-white">TAVEEL</strong>? It's a combination of both our last names—<strong className="text-white">
                  Tav</strong>akoli and Fad<strong className="text-white">eel</strong>. A symbol of the partnership that started in a high school 
                  hallway and evolved into a mission to help businesses dominate their markets.
                </p>

                <p className="text-2xl font-bold text-[#10B981] italic pt-4">
                  "We don't just create ads. We create momentum."
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Why We're Different */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">WHY WE'RE <span className="text-[#10B981]">DIFFERENT</span></h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-[#2d2d2d] border-gray-700 hover:border-[#10B981]/50 transition-all duration-300">
              <CardContent className="p-8">
                <div className="text-[#10B981] mb-4">
                  <Users className="w-12 h-12" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Founded By Hustlers, Not Suits</h3>
                <p className="text-gray-300 leading-relaxed">
                  We're not trust-fund kids who bought an agency. We're self-made entrepreneurs who learned marketing 
                  the hard way—through trial, error, and thousands of campaigns. We understand what it's like to 
                  bootstrap and scale because we've lived it.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#2d2d2d] border-gray-700 hover:border-[#10B981]/50 transition-all duration-300">
              <CardContent className="p-8">
                <div className="text-[#10B981] mb-4">
                  <Target className="w-12 h-12" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Data-Driven, Not Guesswork</h3>
                <p className="text-gray-300 leading-relaxed">
                  We've analyzed what works across 100+ businesses. Our AI doesn't just find trends—it predicts them. 
                  Every ad we create is backed by real data, not creative intuition or "best practices" from 2015.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#2d2d2d] border-gray-700 hover:border-[#10B981]/50 transition-all duration-300">
              <CardContent className="p-8">
                <div className="text-[#10B981] mb-4">
                  <Heart className="w-12 h-12" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">We Actually Care About Your Success</h3>
                <p className="text-gray-300 leading-relaxed">
                  Your success is our success. We're not here to lock you into long contracts and deliver mediocre 
                  results. We offer a free 7-day trial because we're confident you'll see the difference. No gimmicks. 
                  Just results.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-[#2d2d2d] border-gray-700 hover:border-[#10B981]/50 transition-all duration-300">
              <CardContent className="p-8">
                <div className="text-[#10B981] mb-4">
                  <TrendingUp className="w-12 h-12" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">In-House Team, Not Outsourced</h3>
                <p className="text-gray-300 leading-relaxed">
                  Everything is done in-house—photography, videography, ad creation, strategy. We don't outsource to 
                  freelancers on Fiverr. You get consistent, high-quality work from a team that knows your business.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#2d2d2d] to-[#374151]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-bold mb-6">READY TO SCALE WITH US?</h2>
          <p className="text-xl text-gray-200 mb-12">
            Join the 100+ businesses that trust Mohanned and Omid's proven system. Start your free 7-day trial today.
          </p>
          <Button 
            onClick={() => window.location.href = '/'}
            className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-6 text-lg"
          >
            Start Free Trial
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[#1a1a1a] border-t border-gray-700">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400">© 2025 Taveel. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default WhyTaveel;
